import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Badge, Button } from "../components/ui";
import { ShieldCheck, LifeBuoy, HardDriveDownload, KeyRound, Lock, RefreshCw, CheckCircle2, XCircle, Download } from "lucide-react";

const APP_LABELS = { family_tree: "Family Tree", benefits: "Benefits Management", currency: "Currency Management", holdings: "Holdings Management" };

function Status({ on, onText, offText }) {
  return on
    ? <Badge color="success"><CheckCircle2 size={12} className="inline" /> {TL(onText)}</Badge>
    : <Badge color="neutral"><XCircle size={12} className="inline" /> {TL(offText)}</Badge>;
}

export default function Privacy() {
  const { user } = useApp();
  const [repair, setRepair] = useState(null);
  const [buffers, setBuffers] = useState([]);

  const load = () => {
    client.get("/repair/settings").then((r) => setRepair(!!r.data?.enabled)).catch(() => {});
    client.get("/live-buffer/status").then((r) => setBuffers((r.data.apps || []).filter((a) => a.app !== "master"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleRepair = async () => {
    const nv = !repair;
    try { await client.put("/repair/settings", { enabled: nv }); setRepair(nv); } catch { /* ignore */ }
  };

  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await client.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-data-export-${(data.exported_at || "").slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ } finally { setExporting(false); }
  };

  const hasRecovery = !!user?.recovery_email && !user?.needs_recovery_email;
  const anyBackupOn = buffers.some((b) => b.enabled);

  return (
    <div className="space-y-6 fadeup" data-testid="privacy-page">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><ShieldCheck size={26} /> {TL("Privacy & control")}</h1>
        <p className="mt-1 text-sm opacity-70 max-w-3xl">{TL("Everything that governs your data and who can touch it, in one place and in plain language. Your data is private to your account — nobody else can see it, and nothing here sends your program any code updates.")}</p>
      </div>

      {/* Support-assisted restore */}
      <Card className="space-y-3" data-testid="privacy-repair">
        <div className="flex flex-wrap items-center gap-2">
          <LifeBuoy size={18} className="text-[var(--primary)]" />
          <h3 className="font-head text-lg">{TL("Support-assisted restore")}</h3>
          <span className="ms-auto"><Status on={!!repair} onText="Enabled" offText="Off (default)" /></span>
        </div>
        <p className="text-sm opacity-70 max-w-3xl">{TL("Off by default. When enabled, a support agent can")} <b>{TL("offer")}</b> {TL("a one-click restore of your own data from your live backup if your instance is corrupted — you still have to click to accept. It never sends program updates and never touches any other account. While off, your app doesn't even check for support offers.")}</p>
        <button type="button" onClick={toggleRepair} data-testid="privacy-repair-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm lift ${repair ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-black/15 dark:border-white/15 opacity-80"}`}>
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${repair ? "bg-[var(--primary)]" : "bg-black/30 dark:bg-white/30"}`} />
          {repair ? TL("Enabled — support may offer restores") : TL("Disabled (default) — no support access")}
        </button>
      </Card>

      {/* Live backup */}
      <Card className="space-y-3" data-testid="privacy-backup">
        <div className="flex flex-wrap items-center gap-2">
          <HardDriveDownload size={18} className="text-[var(--primary)]" />
          <h3 className="font-head text-lg">{TL("Live backup (crash protection)")}</h3>
          <span className="ms-auto"><Status on={anyBackupOn} onText="Active on some apps" offText="Off" /></span>
        </div>
        <p className="text-sm opacity-70 max-w-3xl">{TL("A rolling backup that protects recent entries against crashes or power loss. You control it per application.")}</p>
        {buffers.length === 0 ? (
          <p className="text-sm opacity-60">{TL("Activate an application to use live backup.")}</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="privacy-backup-list">
            {buffers.map((b) => (
              <span key={b.app} className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-2.5 py-1 text-xs dark:border-white/10" data-testid={`privacy-backup-${b.app}`}>
                {TL(APP_LABELS[b.app] || b.app)}: {b.enabled ? <span className="text-emerald-600">{TL("on")}</span> : <span className="opacity-60">{TL("off")}</span>}
              </span>
            ))}
          </div>
        )}
        <div><Link to="/app/sync"><Button variant="ghost" data-testid="privacy-goto-sync"><RefreshCw size={14} className="me-1.5" /> {TL("Manage in Sync & Backup")}</Button></Link></div>
      </Card>

      {/* Data ownership / export */}
      <Card className="space-y-3" data-testid="privacy-export">
        <div className="flex items-center gap-2"><Download size={18} className="text-[var(--primary)]" /><h3 className="font-head text-lg">{TL("Your data")}</h3></div>
        <p className="text-sm opacity-70 max-w-3xl">{TL("Your records belong to you. Download a complete copy of everything across all your applications (people, benefits, currencies, holdings, transactions and settings) as a single portable file — any time, no request needed.")}</p>
        <div><Button onClick={exportData} disabled={exporting} data-testid="privacy-export-btn">
          <Download size={14} className={`me-1.5 ${exporting ? "animate-pulse" : ""}`} /> {exporting ? TL("Preparing…") : TL("Download my data")}
        </Button></div>
      </Card>

      {/* Account security (informational) */}
      <Card className="space-y-3" data-testid="privacy-security">
        <div className="flex items-center gap-2"><Lock size={18} className="text-[var(--primary)]" /><h3 className="font-head text-lg">{TL("Account security")}</h3></div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border border-black/10 px-3 py-2 text-sm dark:border-white/10">
            <span className="flex items-center gap-2"><KeyRound size={15} /> {TL("Two-factor sign-in (MFA)")}</span>
            <Status on={true} onText="Always on" offText="Off" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-black/10 px-3 py-2 text-sm dark:border-white/10" data-testid="privacy-recovery">
            <span className="flex items-center gap-2"><ShieldCheck size={15} /> {TL("Recovery email")}</span>
            <Status on={hasRecovery} onText="Set" offText="Not set" />
          </div>
        </div>
        <p className="text-xs opacity-60">{TL("Every sign-in requires a one-time code (MFA). A recovery email lets you regain access if you're locked out. Manage these on your")} <Link to="/app/account" className="text-[var(--primary)] hover:underline">{TL("Account")}</Link> {TL("page.")}</p>
      </Card>
    </div>
  );
}
